import React from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

import type { OrderType } from '../types';

/**
 * Market | Limit, directly under the leverage slider on both surfaces.
 *
 * Deliberately the same `ToggleGroup` the TP / SL / slippage rows use: it is
 * one more thing armed on the order before it is sent, and it should read as
 * part of that stack rather than as a mode switch bolted above it.
 */
export const OrderTypeTabs: React.FC<{
    value: OrderType;
    onChange: (next: OrderType) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
    <ToggleGroup
        type="single"
        value={value}
        onValueChange={(next) => {
            // Radix reports '' when the pressed item is toggled OFF; an order
            // always has a type, so that is a no-op rather than a third state.
            if (next === 'market' || next === 'limit') onChange(next);
        }}
        className="w-full"
        disabled={disabled}
        aria-label="Order type"
    >
        <ToggleGroupItem value="market" className="flex-1">
            Market
        </ToggleGroupItem>
        <ToggleGroupItem value="limit" className="flex-1">
            Limit
        </ToggleGroupItem>
    </ToggleGroup>
);

/** How far the typed limit sits from the mark, in the user's words. */
export function limitDistanceLabel(
    limitPrice: number | null,
    markPrice: number,
): string | null {
    if (limitPrice === null || !(markPrice > 0)) return null;
    const pct = ((limitPrice - markPrice) / markPrice) * 100;
    if (Math.abs(pct) < 0.005) return 'at the mark';
    return `${Math.abs(pct).toFixed(2)}% ${pct > 0 ? 'above' : 'below'} mark`;
}

/** Parsed limit price, or null while the field is empty or unusable. */
export function parseLimitPriceInput(raw: string): number | null {
    const value = Number.parseFloat(raw.trim());
    return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * The price the GTC entry will rest at.
 *
 * Text, not a number, in the store: a half-typed decimal ("81.") reads as ""
 * from a number input, and re-deriving the text from the parsed number each
 * keystroke is what eats the decimal point as you type it.
 */
export const LimitPriceField: React.FC<{
    value: string;
    onChange: (next: string) => void;
    markPrice: number;
    disabled?: boolean;
    /** formats the "use the mark" button's price and nothing else. */
    formatPrice: (value: number) => string;
    compact?: boolean;
}> = ({ value, onChange, markPrice, disabled, formatPrice, compact }) => {
    const parsed = parseLimitPriceInput(value);
    const distance = limitDistanceLabel(parsed, markPrice);

    return (
        <div>
            <span
                className={cn(
                    'mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                    compact && 'mb-1',
                )}
            >
                Limit price
            </span>
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min={0}
                    aria-label="Limit price"
                    value={value}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={
                        markPrice > 0
                            ? `Price (mark ${formatPrice(markPrice)})`
                            : 'Price'
                    }
                    className="h-10 min-w-0 flex-1 rounded-lg border border-border bg-input px-2.5 font-mono text-sm font-bold tabular-nums text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50"
                />
                <button
                    type="button"
                    disabled={disabled || !(markPrice > 0)}
                    onClick={() => onChange(String(markPrice))}
                    className="h-10 shrink-0 cursor-pointer rounded-lg border border-border bg-input px-3 font-mono text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Mark
                </button>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
                {distance ??
                    'The order rests until the market reaches this price.'}
            </p>
        </div>
    );
};
